<script setup lang="ts">
import { AppTopbar } from '@/components/layout';
import { defineAsyncComponent, hydrateOnIdle, hydrateOnVisible } from 'vue';
import { useInstallPrompt } from '~/composables/useInstallPrompt';

// Delayed hydration (Vue 3.5's `hydrate` option on `defineAsyncComponent`) is
// used below instead of the `<LazyFoo hydrate-on-visible>` auto-import sugar
// because this app runs with `components: false` — nothing is auto-imported,
// so the `Lazy*` component variants Nuxt generates for that sugar don't
// exist here. SSR output is unaffected either way: these components still
// render their full markup on the server; only the client-side hydration of
// that markup is deferred, which is what was blowing out LCP render-delay
// under mobile CPU throttle (main-thread contention hydrating the whole app
// at once).

// Below the fold on every page and pure links/static content — the SSR HTML
// works fine on its own until a visitor actually scrolls to it, so hydration
// can wait until it's visible instead of competing for the initial
// hydration wave.
const AppFooter = defineAsyncComponent({
  loader: () => import('@/components/layout/AppFooter.vue'),
  hydrate: hydrateOnVisible(),
});

// Async for the chunk split, but hydrated in the INITIAL wave — no `hydrate`
// option, on purpose. AppConfirmDialog wraps PrimeVue's <ConfirmDialog>,
// which subscribes to the ConfirmationService event bus in its onMounted; a
// `confirm.require()` fired before that subscription exists is silently
// dropped. That's the same mount-time-listener trap that kept the Toast out
// of this block — "renders nothing until opened" was true of its pixels, not
// of its listener. Idle hydration under mobile main-thread contention can
// lag by seconds, exactly when an early destructive-action tap would need
// the dialog.
const AppConfirmDialog = defineAsyncComponent({
  loader: () => import('@/components/base/AppConfirmDialog.vue'),
});

// A `v-show`-hidden mobile overlay — invisible until the AppTopbar burger
// toggles `useLayoutStore().isSidebarActive`. Idle-time hydration still
// finishes well within the time it takes a user to notice and tap the
// burger, so the toggle stays responsive while the sidebar is kept out of
// the initial hydration wave. (Verified interactively — see PR notes.)
const AppSidebar = defineAsyncComponent({
  loader: () => import('@/components/layout/AppSidebar.vue'),
  hydrate: hydrateOnIdle(),
});

// The install banner is `position: fixed` at the viewport bottom and the footer
// is the last thing on the page, so the space has to be reserved here — below
// the footer — not inside the page, or the banner covers the footer's links.
// `useInstallPrompt` is a module singleton, so reading it a second time is free.
const { isBannerOnScreen } = useInstallPrompt();
</script>

<template>
  <div class="default-layout relative w-full">
    <AppTopbar />
    <AppSidebar />

    <!-- No page-size here: pages own their width (PageHero bands are
         full-bleed; constrained pages add their own container). -->
    <main class="default-layout__main">
      <NuxtPage :page-key="(route) => route.fullPath" />
    </main>

    <AppFooter />

    <!-- ClientOnly, not a plain `v-if`: the banner's visibility is decided from
         browser-only signals, so rendering the spacer during SSR would be a
         hydration mismatch. -->
    <ClientOnly>
      <div
        v-if="isBannerOnScreen"
        class="default-layout__install-banner-space"
        aria-hidden="true"
        data-testid="install-banner-space"
      />
    </ClientOnly>

    <Toast />
    <AppConfirmDialog />
  </div>
</template>

<style scoped lang="scss">
.default-layout {
  // Sticky footer: the container is at least the viewport height, and main
  // grows to fill the leftover space. Short pages -> footer rests at the
  // bottom of the viewport (no scroll); long pages -> main grows with its
  // content and pushes the footer below it (no overlap).
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  padding-top: var(--header-height);

  &__main {
    flex: 1 0 auto;
  }

  // 88px is the banner's Figma height (16px + 48px content + 24px), plus the
  // same safe-area allowance the banner's bottom padding carries, so the two
  // stay in agreement. Mobile-only, matching the banner's own breakpoint.
  &__install-banner-space {
    flex: 0 0 auto;
    display: none;

    @media (max-width: 1023px) {
      display: block;
      height: calc(88px + env(safe-area-inset-bottom));
    }
  }
}
</style>
